'use client';
import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Users, DollarSign, Percent, UserMinus } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const kpiData = [
  {
    id: 'kpi-revenue',
    label: 'Total Revenue',
    value: '$4,287,340',
    rawValue: 4287340,
    change: +12.4,
    period: 'vs last month',
    icon: DollarSign,
    color: 'primary',
    trend: 'up',
    sparkData: [310, 290, 345, 360, 380, 355, 410, 428],
  },
  {
    id: 'kpi-users',
    label: 'Active Users',
    value: '84,291',
    rawValue: 84291,
    change: +8.7,
    period: 'vs last month',
    icon: Users,
    color: 'positive',
    trend: 'up',
    sparkData: [720, 690, 740, 760, 810, 790, 835, 842],
  },
  {
    id: 'kpi-conversion',
    label: 'Conversion Rate',
    value: '3.84%',
    rawValue: 3.84,
    change: -0.6,
    period: 'vs last month',
    icon: Percent,
    color: 'warning',
    trend: 'down',
    sparkData: [4.2, 4.1, 3.9, 4.0, 3.8, 3.7, 3.9, 3.84],
  },
  {
    id: 'kpi-churn',
    label: 'Churn Rate',
    value: '2.31%',
    rawValue: 2.31,
    change: +0.42,
    period: 'vs last month',
    icon: UserMinus,
    color: 'negative',
    trend: 'up-bad',
    sparkData: [1.8, 1.9, 2.0, 1.95, 2.1, 2.2, 2.25, 2.31],
  },
];

const colorMap: Record<string, string> = {
  primary: 'bg-primary/10 text-primary',
  positive: 'bg-positive/10 text-positive',
  warning: 'bg-warning/10 text-warning',
  negative: 'bg-negative/10 text-negative',
};

const cardBorderMap: Record<string, string> = {
  primary: 'border-primary/20',
  positive: 'border-positive/20',
  warning: 'border-warning/20',
  negative: 'border-negative/20 bg-negative/5',
};

export default function KPIBentoGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {kpiData.map((kpi) => (
        <KPICard key={kpi.id} kpi={kpi} />
      ))}
    </div>
  );
}

function KPICard({ kpi }: { kpi: typeof kpiData[0] }) {
  const Icon = kpi.icon;
  const isPositiveTrend = kpi.trend === 'up' || kpi.trend === 'up-bad';
  const isBadTrend = kpi.trend === 'down' || kpi.trend === 'up-bad';

  const deltaColor = isBadTrend ? 'text-negative bg-negative/10' : 'text-positive bg-positive/10';
  const DeltaIcon = isBadTrend ? TrendingDown : TrendingUp;

  const maxSpark = Math.max(...kpi.sparkData);
  const minSpark = Math.min(...kpi.sparkData);
  const range = maxSpark - minSpark || 1;

  const sparkPoints = kpi.sparkData.map((v, i) => {
    const x = (i / (kpi.sparkData.length - 1)) * 100;
    const y = 100 - ((v - minSpark) / range) * 80 - 10;
    return `${x},${y}`;
  }).join(' ');

  const sparkColor =
    kpi.color === 'primary' ? 'var(--primary)' :
    kpi.color === 'positive' ? 'var(--positive)' :
    kpi.color === 'warning' ? 'var(--warning)' :
    'var(--negative)';

  return (
    <div className={`card-surface hover-lift p-5 ${cardBorderMap[kpi.color]} relative overflow-hidden`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[kpi.color]}`}>
          <Icon size={18} />
        </div>
        <span className={`flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${deltaColor}`}>
          <DeltaIcon size={11} />
          {Math.abs(kpi.change)}%
        </span>
      </div>

      <p className="label-caps text-muted-foreground mb-1">{kpi.label}</p>
      <p className="metric-value text-3xl text-foreground">{kpi.value}</p>
      <p className="text-xs text-muted-foreground mt-1">{kpi.period}</p>

      {/* Sparkline */}
      <div className="mt-4 h-10">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          <polyline
            points={sparkPoints}
            fill="none"
            stroke={sparkColor}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
    </div>
  );
}